import { trackApparelBrandRetailerPdpWithMixedShippingView } from "@faire/web-api/events/apparelBrand/view/retailerPdpWithMixedShipping";
import { trackProductsPageViaImpressionView } from "@faire/web-api/events/products/view/pageViaImpression";
import { IDwellTime } from "@faire/web-api/indigofair/data/IDwellTime";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { QueryParams } from "@faire/web/common/query-params/QueryParams";
import { trackEvent } from "@faire/web/common/trackEvent";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import React, { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  recordLandingActivity,
  recordSignupActivity,
} from "@faire/retailer/actions/user/recordLoggedOutActivity";
import { useAppSignUpPageResultsUpdateEffect } from "@faire/retailer/components/AppSignUp/AppSignUpPageResultsProvider";
import { PageInitialized, ViewProduct } from "@faire/retailer/events";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { useIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { useDwellService } from "@faire/retailer/services/DwellService";
import { PageLoadEvent } from "@faire/retailer/services/performance/const";
import { PerformanceMarker } from "@faire/retailer/services/performance/PerformanceMarker";
import { assignSettingProductPageReactQuery } from "@faire/retailer/settings/getSettingProductPageReactQuery";
import {
  useAssignSettingFaireDirectPDPSignup,
  useSettingFaireDirectPDPSignup,
} from "@faire/retailer/settings/useSettingFaireDirectPDPSignup";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { AppModalStore } from "@faire/retailer/stores/ui/AppModalStore";
import { useAppSnackbarStore } from "@faire/retailer/stores/ui/AppSnackbarStore";
import {
  getProductDetails,
  getProductName,
  isMixedProduct,
} from "@faire/retailer/views/product/utils/productUtils";

export const ProductPageEffects: React.FC<{
  productToken: string;
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  queryParams: QueryParams;
}> = ({ brand, product, queryParams, productToken }) => {
  useEffect(() => {
    PageInitialized.publish();
  }, []);

  useEffect(() => {
    PerformanceMarker.get().mark(PageLoadEvent.pageStart);
  }, []);

  useEffect(() => {
    ViewProduct.publish(getProductDetails(product));
  }, [product]);

  const { pathname } = useLocation();
  const history = useHistory();

  const recordLoggedOutActivity = useEffectEvent(() => {
    const loggedOutActivity = {
      product_token: product?.token,
      brand_token: brand?.token,
    };
    recordLandingActivity(loggedOutActivity, history);
    recordSignupActivity(loggedOutActivity, pathname);
  });

  useEffect(recordLoggedOutActivity, [recordLoggedOutActivity]);

  const impressionId = queryParams.get("impressionId");
  useEffect(() => {
    if (product.token && impressionId) {
      trackProductsPageViaImpressionView(product.token, impressionId);
    }
  }, [product.token, impressionId]);

  const { start: startDwell, stop: stopDwell } = useDwellService([
    "start",
    "stop",
  ]);
  useEffect(() => {
    startDwell(product.token ?? "", IDwellTime.Type.PRODUCT);
    return () => {
      stopDwell();
    };
  }, [startDwell, stopDwell, product.token]);

  const [_, setAppSnackbarStore] = useAppSnackbarStore([]);
  useEffect(() => {
    setAppSnackbarStore({ productTitle: getProductName(product) });
    return () => {
      setAppSnackbarStore({ productTitle: undefined });
    };
  }, [product, setAppSnackbarStore]);

  useEffect(() => {
    assignSettingProductPageReactQuery();
  }, []);

  const { retailerToken } = useRetailerStore(["retailerToken"]);
  const isBrandPreview = useIsBrandPreview();
  useEffect(() => {
    if (!isBrandPreview && product.token && isMixedProduct(product)) {
      trackApparelBrandRetailerPdpWithMixedShippingView(
        retailerToken ?? "",
        product.token
      );
    }
  }, [isBrandPreview, product, retailerToken]);

  const trackEventToken = queryParams.get("trackEventToken");
  useEffect(() => {
    const eventPath = `/retailer/${retailerToken}`;
    // IMPORTANT - Do not remove. BE uses this to calculate product page visits.
    trackEvent(`${eventPath}/product-page/${productToken}`);
    if (trackEventToken) {
      trackEvent(
        `${eventPath}/product-page/${productToken}/${trackEventToken}`
      );
    }
  }, [productToken, trackEventToken, retailerToken]);

  useAppSignUpPageResultsUpdateEffect({ pdpProduct: product });

  useAutoSignUpEffects(queryParams);

  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();
  const assignSettingFaireDirectPDPSignup =
    useAssignSettingFaireDirectPDPSignup();
  useEffect(() => {
    if (!isLoggedInRetailerOrBrandPreview && isFaireDirectActiveInLoggedOut) {
      assignSettingFaireDirectPDPSignup();
    }
  }, [
    assignSettingFaireDirectPDPSignup,
    isFaireDirectActiveInLoggedOut,
    isLoggedInRetailerOrBrandPreview,
  ]);

  return null;
};

const useAutoSignUpEffects = (queryParams: QueryParams) => {
  const queryParamSignUp = queryParams.get("signUp");

  const settingFaireDirectPDPSignup = useSettingFaireDirectPDPSignup();
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();

  const autoSignUpTimeoutHandleRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Handle the case when there is another sign-up modal shown before our timeout executes.
    // If the user already closed the modal, we want to react to the the query param changing,
    // and cancel the timer so we don't show the sign-up modal again and annoy the user.
    clearTimeout(autoSignUpTimeoutHandleRef.current);
  }, [queryParamSignUp]);

  useEffect(() => {
    if (isLoggedInRetailerOrBrandPreview || settingFaireDirectPDPSignup) {
      return;
    }

    if (isFaireDirectActiveInLoggedOut) {
      autoSignUpTimeoutHandleRef.current = setTimeoutIfPossible(() => {
        if (!AppModalStore.get().isModalShowing) {
          popSignUp(SignUpType.FAIRE_DIRECT, () => null, {
            element: SignUpElement.AUTO_POPUP,
            sourceId: "faire_direct_auto_signup",
          });
        }
      }, 5000);
    }

    return () => {
      clearTimeout(autoSignUpTimeoutHandleRef.current);
    };
  }, [
    isFaireDirectActiveInLoggedOut,
    isLoggedInRetailerOrBrandPreview,
    settingFaireDirectPDPSignup,
  ]);
};
